import { useEffect, useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import {
  SidePanel,
  ButtonPrimary,
  ButtonPseudo,
  ajvResolver,
  ValidatorsProps,
  ResponseError,
  Popup,
  responseErrorsResolver,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import {
  useLazyGetEmployeesBaseDictionariesQuery,
  useLazyGetActiveCurrenciesQuery,
  useLazyGetAdministrationCountriesQuery,
  useLazyGetEmployeesDictionariesQuery,
} from '../../store/dictionaries/service';
import { changePanelState } from './store/slices/roles';
import ROUTES from '../../routes';
import RoleCreateForm from '../../components/user-roles/panel/panel-role-create';
import {
  useCreateRoleMutation,
  useLazyGetRolesQuery,
} from './store/services/roles';
import { CreateRoleFormValues, PermissionEnum, RoleDetails } from './types';
import Expandable from '../../components/user-roles/expandable/expandable';

const RoleCreateHOC = (): React.JSX.Element => {
  const {
    userRoles: {
      roles: { panelIsOpen, validators },
    },
    administrationDictionaries: {
      users: usersOptions,
      activePermissions,
      roles,
      activeCurrencies,
      countries,
      users,
    },
    notificationSettings: {
      templates: { templates },
    },
  } = useAppSelector((state) => state);
  const [dirtyPopupVisible, setDirtyPopupVisible] = useState(false);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [getEmployeesDictionaries] = useLazyGetEmployeesDictionariesQuery();
  const [getEmployeesBaseDictionaries] =
    useLazyGetEmployeesBaseDictionariesQuery();
  const [getActiveCurrencies] = useLazyGetActiveCurrenciesQuery();
  const [getEmployeesCountries] = useLazyGetAdministrationCountriesQuery();
  const [create, { isLoading, isSuccess }] = useCreateRoleMutation();
  const [getRoles] = useLazyGetRolesQuery();
  const [globalError, setGlobalError] = useState('');

  const getRolesAction = useCallback(() => {
    getRoles('');
  }, []);

  const {
    control,
    getValues,
    reset,
    formState: { errors, isValid, isSubmitted, isDirty },
    setError,
  } = useForm<CreateRoleFormValues>({
    defaultValues: { name: '', userIds: [] },
    resolver: ajvResolver(
      validators?.createRoleTemplateSchema as ValidatorsProps,
    ),
  });

  const handleClose = useCallback(() => {
    dispatch(changePanelState(false));
    reset();

    setTimeout(() => {
      navigate(ROUTES.USER_ROLES);
    }, 100);
  }, [dispatch]);

  const onSubmit = async () => {
    setGlobalError('');

    const values = getValues();

    const response = (await create(values)) as {
      data?: RoleDetails;
      error: ResponseError;
    };

    if (response.error) {
      responseErrorsResolver({
        errors: response.error,
        setError,
        setGlobalError,
      });
    }
  };

  useEffect(() => {
    if (!roles) {
      getEmployeesBaseDictionaries();
    }

    if (!activeCurrencies) {
      getActiveCurrencies();
    }

    if (!countries) {
      getEmployeesCountries();
    }
  }, [roles, activeCurrencies, countries]);

  useEffect(() => {
    dispatch(changePanelState(true));

    return () => {
      dispatch(changePanelState(false));
    };
  }, [dispatch]);

  useEffect(() => {
    if (activePermissions?.includes(PermissionEnum.USERS_LIST) && !users) {
      getEmployeesDictionaries();
    }
  }, [activePermissions, users]);

  useEffect(() => {
    if (isSuccess) {
      getRolesAction();
      handleClose();
    }
  }, [isSuccess, handleClose]);

  return (
    <>
      <SidePanel
        closeOnClickOutside
        isOpen={panelIsOpen}
        onClose={isDirty ? () => setDirtyPopupVisible(true) : handleClose}
        title="Add role"
        errorSidepanel={globalError}
        sidepanelExpandedKey="role-details"
        footer={
          <>
            <ButtonPrimary
              text={isLoading ? 'Loading...' : 'Add'}
              onClick={onSubmit}
              bold
              disabled={isLoading || (!isValid && isSubmitted)}
            />
            <ButtonPseudo
              text="Cancel"
              onClick={isDirty ? () => setDirtyPopupVisible(true) : handleClose}
              bold
            />
          </>
        }
      >
        <>
          <Expandable>
            <ul>
              <li>Dashboard view</li>
              <li>Accounts view</li>
              <li>Customers view</li>
              <li>AML & KYC view</li>
              <li>Transactions view</li>
              <li>Cards view</li>
              <li>Reports view</li>
              <li>Banks view</li>
              <li>Fees & Tariffs view</li>
              <li>Administration view</li>
            </ul>
          </Expandable>

          <RoleCreateForm
            control={control}
            errors={errors}
            userList={usersOptions}
            templatesVisible={activePermissions?.includes(
              PermissionEnum.NOTIFICATIONS_TYPES_TEMPLATE_LIST,
            )}
            templates={templates?.map((item) => ({
              name: item.name,
              value: item.id,
            }))}
          />
        </>
      </SidePanel>
      <Popup
        title="You have unsaved changes that will be lost"
        description="Are you sure you want to close this form?"
        onCancelClick={() => setDirtyPopupVisible(false)}
        onOkClick={handleClose}
        cancelButtonText="Cancel"
        okButtonText="Close"
        isVisible={dirtyPopupVisible}
        parent={document.getElementById('dirty-popup') as HTMLElement}
      />
      <div id="dirty-popup" />
    </>
  );
};

export default RoleCreateHOC;
